// @erp/module-lib/fe · the query-key factory (DEV-GUIDE-002 §3.5 — ad-hoc string
// keys in components are BANNED: targeted invalidation dies on a typo). Every
// query and every mutation's invalidateQueries reads its key from here, so a
// rename can never silently desync a screen's cache.
//
// Shape: hierarchical tuples rooted at ['lib'], one branch per routed domain —
//   ['lib']                              → libraryKeys.all           (nuke everything)
//   ['lib','patrons']                    → libraryKeys.patrons.all() (a whole domain)
//   ['lib','patrons','list', filters]    → libraryKeys.patrons.list(f)
//   ['lib','patrons','detail', id]       → libraryKeys.patrons.detail(id)
// Prefix-invalidation is the point: invalidating ['lib','patrons'] sweeps every
// list page AND every detail under it, because TanStack matches by key prefix.
//
// Canonical property order (DEV-GUIDE-002 §8.3): TanStack's hashKey sorts object
// keys, so a filters object hashes identically regardless of insertion order —
// but callers should still build filters in one order to keep devtools readable.
//
// Filter params stay GENERIC (`<F>`): the concrete schema query type flows in at
// the call site (queries.ts passes MemberListQuery, BookListQuery, …), so the key
// is precise without this file importing all 13 domains' query contracts.

/** The one root. Everything below composes from it; `all` alone invalidates the module. */
const ROOT = ["lib"] as const;

export const libraryKeys = {
  all: ROOT,

  // ── Patrons (members; staff live behind the staff seam) ──
  patrons: {
    all: () => [...ROOT, "patrons"] as const,
    list: <F>(filters: F) => [...ROOT, "patrons", "list", filters] as const,
    detail: (id: string) => [...ROOT, "patrons", "detail", id] as const,
    /** Derived circulation counters — separate key so a detail edit needn't refetch counters and vice-versa. */
    account: (id: string) => [...ROOT, "patrons", "account", id] as const,
    groups: () => [...ROOT, "patrons", "groups"] as const,
    /** GET /patrons/cards/stats — the card register's derived states. Under patrons.all(), so a suspend/reactivate refreshes it. */
    cardStats: () => [...ROOT, "patrons", "cards", "stats"] as const,
  },

  // ── Catalogue (books/copies + the authority registries + FTS search) ──
  catalogue: {
    all: () => [...ROOT, "catalogue"] as const,
    stats: () => [...ROOT, "catalogue", "stats"] as const,
    /** Bibliographic lookup by scanned/typed code — keyed by the query itself. */
    lookup: (q: string) => [...ROOT, "catalogue", "lookup", q] as const,
    /** Author authority lookup (name / ORCID / ISNI). */
    authorLookup: (q: string) => [...ROOT, "catalogue", "lookup", "author", q] as const,
    /** Publisher name search (Crossref members + OL imprints). */
    publisherLookup: (q: string) => [...ROOT, "catalogue", "lookup", "publisher", q] as const,
    search: <F>(query: F) => [...ROOT, "catalogue", "search", query] as const,
    books: {
      all: () => [...ROOT, "catalogue", "books"] as const,
      list: <F>(filters: F) => [...ROOT, "catalogue", "books", "list", filters] as const,
      detail: (id: string) => [...ROOT, "catalogue", "books", "detail", id] as const,
      copies: (id: string) => [...ROOT, "catalogue", "books", "copies", id] as const,
    },
    authors: {
      all: () => [...ROOT, "catalogue", "authors"] as const,
      list: <F>(filters: F) => [...ROOT, "catalogue", "authors", "list", filters] as const,
      detail: (id: string) => [...ROOT, "catalogue", "authors", "detail", id] as const,
      /** Possible-duplicate clusters (§6). Under authors.all(), so a merge sweeps it. */
      duplicates: (limit: number) => [...ROOT, "catalogue", "authors", "duplicates", limit] as const,
    },
    publishers: {
      all: () => [...ROOT, "catalogue", "publishers"] as const,
      list: <F>(filters: F) => [...ROOT, "catalogue", "publishers", "list", filters] as const,
      detail: (id: string) => [...ROOT, "catalogue", "publishers", "detail", id] as const,
    },
    subjects: () => [...ROOT, "catalogue", "subjects"] as const,
    tags: () => [...ROOT, "catalogue", "tags"] as const,
    /** MARC bulk-import history (lib_import_batch — keyset, newest first). */
    importBatches: {
      all: () => [...ROOT, "catalogue", "import-batches"] as const,
      list: <F>(filters: F) => [...ROOT, "catalogue", "import-batches", "list", filters] as const,
    },
  },

  // ── Circulation (the POS core: lookup + the issue/return/renew loop) ──
  circulation: {
    all: () => [...ROOT, "circulation"] as const,
    /** Barcode desk lookup — its own leaf so a scan poke doesn't invalidate loan lists. */
    lookup: (barcode: string) => [...ROOT, "circulation", "lookup", barcode] as const,
    transit: {
      all: () => [...ROOT, "circulation", "transit"] as const,
      list: <F>(filters: F) => [...ROOT, "circulation", "transit", "list", filters] as const,
    },
  },

  // ── Loans (the circulation ledger — /circulation/loans; its own namespace per spec) ──
  loans: {
    all: () => [...ROOT, "loans"] as const,
    list: <F>(filters: F) => [...ROOT, "loans", "list", filters] as const,
    detail: (id: string) => [...ROOT, "loans", "detail", id] as const,
  },

  // ── Holds / reservations ──
  // `stats()` (2026-09-22) keys GET /holds/stats — the queue's institution-wide
  // counts the list's stat tiles read. A leaf under the SAME domain prefix, so
  // every write that sweeps holds.all() (place, mark ready, complete, expire, and
  // the return-path promotion) refreshes the figures with the page. The same
  // pattern lands on patrons/cards, audit, transit, serials and stocktake below.
  holds: {
    all: () => [...ROOT, "holds"] as const,
    list: <F>(filters: F) => [...ROOT, "holds", "list", filters] as const,
    detail: (id: string) => [...ROOT, "holds", "detail", id] as const,
    stats: () => [...ROOT, "holds", "stats"] as const,
  },

  // ── Fines / fees ──
  fines: {
    all: () => [...ROOT, "fines"] as const,
    list: <F>(filters: F) => [...ROOT, "fines", "list", filters] as const,
    detail: (id: string) => [...ROOT, "fines", "detail", id] as const,
  },

  // ── Inventory exceptions (copies flagged Lost / Damaged) ──
  inventory: {
    all: () => [...ROOT, "inventory"] as const,
    flagged: () => [...ROOT, "inventory", "flagged"] as const,
  },

  // ── Institution policy (the circulation matrix + the closure calendar) ──
  policy: {
    all: () => [...ROOT, "policy"] as const,
    circulationRules: () => [...ROOT, "policy", "circulation-rules"] as const,
    calendarExceptions: () => [...ROOT, "policy", "calendar-exceptions"] as const,
  },

  // ── Memberships (derived dues + the payment ledger + revenue) ──
  memberships: {
    all: () => [...ROOT, "memberships"] as const,
    members: <F>(filters: F) => [...ROOT, "memberships", "members", filters] as const,
    payments: <F>(filters: F) => [...ROOT, "memberships", "payments", filters] as const,
    revenue: () => [...ROOT, "memberships", "revenue"] as const,
    /** Plan registry — the whole list; the API does not page it. */
    plans: () => [...ROOT, "memberships", "plans"] as const,
  },

  // ── Audit log (append-only; admin read) ──
  audit: {
    all: () => [...ROOT, "audit"] as const,
    list: <F>(filters: F) => [...ROOT, "audit", "list", filters] as const,
    stats: () => [...ROOT, "audit", "stats"] as const,
  },

  // ── Institution settings (the /settings console singleton) ──
  settings: {
    all: () => [...ROOT, "settings"] as const,
    institution: () => [...ROOT, "settings", "institution"] as const,
  },

  // ── Parental controls (restriction profiles + guardian links) ──
  parental: {
    all: () => [...ROOT, "parental"] as const,
    profiles: () => [...ROOT, "parental", "profiles"] as const,
    links: () => [...ROOT, "parental", "links"] as const,
  },

  // ── Reports (read-only aggregates behind /reports/*, all lib:reports) ──
  reports: {
    all: () => [...ROOT, "reports"] as const,
    overview: () => [...ROOT, "reports", "overview"] as const,
    circulation: () => [...ROOT, "reports", "circulation"] as const,
    catalogue: () => [...ROOT, "reports", "catalogue"] as const,
    fines: () => [...ROOT, "reports", "fines"] as const,
    members: () => [...ROOT, "reports", "members"] as const,
    acquisitions: () => [...ROOT, "reports", "acquisitions"] as const,
    digital: () => [...ROOT, "reports", "digital"] as const,
  },

  // ── Locations (library → branch → zone → stack → shelf hierarchy) ──
  locations: {
    all: () => [...ROOT, "locations"] as const,
    libraries: () => [...ROOT, "locations", "libraries"] as const,
    library: (id: string) => [...ROOT, "locations", "libraries", "detail", id] as const,
    branches: (libraryId?: string) => [...ROOT, "locations", "branches", libraryId ?? null] as const,
    zones: (branchId?: string) => [...ROOT, "locations", "zones", branchId ?? null] as const,
    stacks: (zoneId?: string) => [...ROOT, "locations", "stacks", zoneId ?? null] as const,
    shelfHoldings: (id: string) => [...ROOT, "locations", "shelves", "holdings", id] as const,
    shelves: (stackId?: string) => [...ROOT, "locations", "shelves", stackId ?? null] as const,
  },

  // ── Digital resources (ebooks + reading sessions + storage meters) ──
  digital: {
    all: () => [...ROOT, "digital"] as const,
    ebooks: {
      all: () => [...ROOT, "digital", "ebooks"] as const,
      list: <F>(filters: F) => [...ROOT, "digital", "ebooks", "list", filters] as const,
      detail: (id: string) => [...ROOT, "digital", "ebooks", "detail", id] as const,
    },
    sessions: <F>(filters: F) => [...ROOT, "digital", "sessions", filters] as const,
    storage: () => [...ROOT, "digital", "storage"] as const,
    /** ONE display asset's inline presigned GET, by stored key (cover/image thumbnails). */
    assetUrl: (key: string) => [...ROOT, "digital", "asset-url", key] as const,
    /** A BATCH of display-asset URLs — keyed by the SORTED deduped key list so key order never re-keys the cache. */
    assetUrls: (keys: readonly string[]) => [...ROOT, "digital", "asset-urls", keys] as const,
  },

  // ── Serials (subscriptions + Kardex issues) ──
  serials: {
    all: () => [...ROOT, "serials"] as const,
    list: <F>(filters: F) => [...ROOT, "serials", "list", filters] as const,
    stats: () => [...ROOT, "serials", "stats"] as const,
    detail: (id: string) => [...ROOT, "serials", "detail", id] as const,
    issues: <F>(id: string, filters: F) => [...ROOT, "serials", "detail", id, "issues", filters] as const,
  },

  // ── Inter-library transit (top-level per spec; the desk ship/receive log) ──
  transit: {
    all: () => [...ROOT, "transit"] as const,
    list: <F>(filters: F) => [...ROOT, "transit", "list", filters] as const,
    stats: () => [...ROOT, "transit", "stats"] as const,
  },

  // ── Stocktake / audit-reconciliation ──
  stocktake: {
    all: () => [...ROOT, "stocktake"] as const,
    stats: () => [...ROOT, "stocktake", "stats"] as const,
    sessions: <F>(filters: F) => [...ROOT, "stocktake", "sessions", filters] as const,
    session: (id: string) => [...ROOT, "stocktake", "sessions", "detail", id] as const,
    scans: <F>(id: string, filters: F) => [...ROOT, "stocktake", "sessions", "detail", id, "scans", filters] as const,
    alerts: <F>(id: string, filters: F) => [...ROOT, "stocktake", "sessions", "detail", id, "alerts", filters] as const,
  },

  // ── Acquisitions (vendors / budget lines / POs / goods receipts) ──
  acquisitions: {
    all: () => [...ROOT, "acquisitions"] as const,
    vendors: <F>(filters: F) => [...ROOT, "acquisitions", "vendors", filters] as const,
    budgetLines: <F>(filters: F) => [...ROOT, "acquisitions", "budget-lines", filters] as const,
    purchaseOrders: {
      all: () => [...ROOT, "acquisitions", "purchase-orders"] as const,
      list: <F>(filters: F) => [...ROOT, "acquisitions", "purchase-orders", "list", filters] as const,
      detail: (id: string) => [...ROOT, "acquisitions", "purchase-orders", "detail", id] as const,
    },
    receivable: <F>(filters: F) => [...ROOT, "acquisitions", "receivable", filters] as const,
    goodsReceipts: <F>(filters: F) => [...ROOT, "acquisitions", "goods-receipts", filters] as const,
    goodsReceipt: (id: string) => [...ROOT, "acquisitions", "goods-receipts", "detail", id] as const,
  },

  // ── OPAC (the PUBLIC catalogue — anonymous-capable reads behind the server's
  //    publicBrowse-or-lib:opac gate). A SEPARATE branch from the staff catalogue
  //    on purpose: a public member browse must never share a cache entry with a
  //    staff list (different projection, different gate), so a staff book write
  //    invalidating catalogue.books.all() leaves the OPAC pages alone and vice-
  //    versa. `settings` is the branding + publicBrowse flag the shell reads pre-
  //    sign-in; an admin PUT sweeps just that leaf. ──
  opac: {
    all: () => [...ROOT, "opac"] as const,
    settings: () => [...ROOT, "opac", "settings"] as const,
    search: <F>(query: F) => [...ROOT, "opac", "search", query] as const,
    subjects: () => [...ROOT, "opac", "subjects"] as const,
    books: {
      all: () => [...ROOT, "opac", "books"] as const,
      list: <F>(filters: F) => [...ROOT, "opac", "books", "list", filters] as const,
      detail: (id: string) => [...ROOT, "opac", "books", "detail", id] as const,
    },
    ebooks: {
      all: () => [...ROOT, "opac", "ebooks"] as const,
      list: <F>(filters: F) => [...ROOT, "opac", "ebooks", "list", filters] as const,
      detail: (id: string) => [...ROOT, "opac", "ebooks", "detail", id] as const,
    },
  },

  // ── /me (the signed-in member's self-service surface). The patron selector is
  //    SERVER-derived from the session (never a client id — the anti-IDOR spine),
  //    so these keys carry NO memberId: there is exactly one member behind them.
  //    A place-hold / cancel / renew sweeps me.all() to re-fetch every /me leaf
  //    (membership + account counters + the loans/holds/fines pages) in one call. ──
  me: {
    all: () => [...ROOT, "me"] as const,
    /** GET /me → { member | null } (the resolved membership, or a link-your-account CTA state). */
    membership: () => [...ROOT, "me", "membership"] as const,
    /** GET /me/account → the derived counters (its own leaf so a write needn't refetch the profile). */
    account: () => [...ROOT, "me", "account"] as const,
    loans: <F>(filters: F) => [...ROOT, "me", "loans", filters] as const,
    holds: <F>(filters: F) => [...ROOT, "me", "holds", filters] as const,
    fines: <F>(filters: F) => [...ROOT, "me", "fines", filters] as const,
  },
} as const;

export type LibraryKeys = typeof libraryKeys;
